/**
 * WorldEffects.ts - Visual effects for the overworld
 * Adds ambient particles, lighting, weather, and polish effects
 */

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  alpha: number;
  type: 'dust' | 'sparkle' | 'leaf' | 'snow' | 'rain';
}

export interface CloudLayer {
  x: number;
  y: number;
  width: number;
  height: number;
  speed: number;
  alpha: number;
}

export class WorldEffects {
  private particles: Particle[] = [];
  private clouds: CloudLayer[] = [];
  private screenWidth: number;
  private screenHeight: number;

  // Time tracking
  private dayNightCycle: number = 0; // 0-1, 0 = midnight, 0.5 = noon
  private ambientTime: number = 0;

  // Screen flash for encounters
  private flashAlpha: number = 0;
  private flashColor: string = '#ffffff';
  private isFlashing: boolean = false;

  // Configuration
  private enableDayNight: boolean = true;
  private enableClouds: boolean = true;
  private enableParticles: boolean = true;

  constructor(screenWidth: number = 256, screenHeight: number = 240) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.initializeClouds();
  }

  private initializeClouds(): void {
    // Create a few cloud layers for parallax effect
    for (let i = 0; i < 5; i++) {
      this.clouds.push({
        x: Math.random() * this.screenWidth * 2,
        y: 10 + Math.random() * 40,
        width: 30 + Math.random() * 50,
        height: 15 + Math.random() * 20,
        speed: 0.1 + Math.random() * 0.2,
        alpha: 0.2 + Math.random() * 0.3,
      });
    }
  }

  /**
   * Update all effects
   */
  update(deltaTime: number): void {
    this.ambientTime += deltaTime;

    // Update day/night cycle (full cycle every 5 minutes)
    if (this.enableDayNight) {
      this.dayNightCycle = (this.dayNightCycle + deltaTime / 300000) % 1;
    }

    // Update clouds
    if (this.enableClouds) {
      this.updateClouds(deltaTime);
    }

    // Update particles
    if (this.enableParticles) {
      this.updateParticles(deltaTime);
    }

    // Update screen flash
    if (this.isFlashing) {
      this.flashAlpha -= deltaTime / 200; // Fade over 200ms
      if (this.flashAlpha <= 0) {
        this.flashAlpha = 0;
        this.isFlashing = false;
      }
    }
  }

  private updateClouds(deltaTime: number): void {
    for (const cloud of this.clouds) {
      cloud.x -= cloud.speed * deltaTime * 0.01;

      // Wrap around
      if (cloud.x + cloud.width < 0) {
        cloud.x = this.screenWidth + 20;
        cloud.y = 10 + Math.random() * 40;
      }
    }
  }

  private updateParticles(deltaTime: number): void {
    // Update existing particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      if (!p) continue;

      p.x += p.vx * deltaTime * 0.01;
      p.y += p.vy * deltaTime * 0.01;
      p.life -= deltaTime;
      p.alpha = Math.max(0, p.life / p.maxLife);

      // Remove dead particles
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }

    // Limit particle count
    while (this.particles.length > 100) {
      this.particles.shift();
    }
  }

  /**
   * Spawn footstep dust when player moves
   */
  spawnFootstepDust(x: number, y: number): void {
    if (!this.enableParticles) return;

    for (let i = 0; i < 3; i++) {
      this.particles.push({
        x: x + Math.random() * 8 - 4,
        y: y + 12,
        vx: (Math.random() - 0.5) * 20,
        vy: -Math.random() * 10 - 5,
        life: 300 + Math.random() * 200,
        maxLife: 500,
        size: 2 + Math.random() * 2,
        color: '#aa9977',
        alpha: 0.6,
        type: 'dust',
      });
    }
  }

  /**
   * Spawn water sparkles at position
   */
  spawnWaterSparkle(x: number, y: number): void {
    if (!this.enableParticles || Math.random() > 0.02) return;

    this.particles.push({
      x: x + Math.random() * 16,
      y: y + Math.random() * 16,
      vx: 0,
      vy: -10,
      life: 500,
      maxLife: 500,
      size: 2,
      color: '#aaddff',
      alpha: 1,
      type: 'sparkle',
    });
  }

  /**
   * Spawn grass/forest leaves
   */
  spawnLeaf(x: number, y: number): void {
    if (!this.enableParticles || Math.random() > 0.005) return;

    this.particles.push({
      x: x + Math.random() * 16,
      y: y,
      vx: 10 + Math.random() * 20,
      vy: 5 + Math.random() * 10,
      life: 2000,
      maxLife: 2000,
      size: 3,
      color: Math.random() > 0.5 ? '#44aa44' : '#668844',
      alpha: 0.8,
      type: 'leaf',
    });
  }

  /**
   * Start screen flash (for encounters)
   */
  startFlash(color: string = '#ffffff'): void {
    this.flashColor = color;
    this.flashAlpha = 1;
    this.isFlashing = true;
  }

  /**
   * Render all effects (call after world/before UI)
   */
  render(ctx: CanvasRenderingContext2D): void {
    // Render clouds (behind everything but can peek through)
    if (this.enableClouds) {
      this.renderClouds(ctx);
    }

    // Render particles
    if (this.enableParticles) {
      this.renderParticles(ctx);
    }

    // Render day/night overlay
    if (this.enableDayNight) {
      this.renderDayNightOverlay(ctx);
    }

    // Render screen flash
    if (this.isFlashing && this.flashAlpha > 0) {
      ctx.save();
      ctx.fillStyle = this.flashColor;
      ctx.globalAlpha = this.flashAlpha;
      ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);
      ctx.restore();
    }
  }

  private renderClouds(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    for (const cloud of this.clouds) {
      ctx.fillStyle = `rgba(255, 255, 255, ${cloud.alpha * 0.3})`;

      // Simple cloud shape (overlapping circles)
      ctx.beginPath();
      ctx.arc(cloud.x + cloud.width * 0.3, cloud.y + cloud.height * 0.5, cloud.height * 0.4, 0, Math.PI * 2);
      ctx.arc(cloud.x + cloud.width * 0.5, cloud.y + cloud.height * 0.3, cloud.height * 0.5, 0, Math.PI * 2);
      ctx.arc(cloud.x + cloud.width * 0.7, cloud.y + cloud.height * 0.5, cloud.height * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private renderParticles(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    for (const p of this.particles) {
      ctx.globalAlpha = p.alpha;

      if (p.type === 'sparkle') {
        // Sparkle effect - cross shape
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x - 1, p.y, 3, 1);
        ctx.fillRect(p.x, p.y - 1, 1, 3);
      } else if (p.type === 'leaf') {
        // Leaf - small diamond
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - p.size / 2);
        ctx.lineTo(p.x + p.size / 2, p.y);
        ctx.lineTo(p.x, p.y + p.size / 2);
        ctx.lineTo(p.x - p.size / 2, p.y);
        ctx.closePath();
        ctx.fill();
      } else {
        // Dust - simple circle
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private renderDayNightOverlay(ctx: CanvasRenderingContext2D): void {
    // Calculate lighting based on time
    // 0 = midnight (dark), 0.25 = sunrise, 0.5 = noon (bright), 0.75 = sunset
    const time = this.dayNightCycle;

    let overlayAlpha = 0;
    let overlayColor = '0, 0, 30'; // Blue tint for night

    if (time < 0.2) {
      // Night (midnight to pre-dawn)
      overlayAlpha = 0.4;
      overlayColor = '0, 0, 40';
    } else if (time < 0.3) {
      // Dawn
      const progress = (time - 0.2) / 0.1;
      overlayAlpha = 0.4 * (1 - progress);
      overlayColor = `${Math.floor(255 * progress * 0.3)}, ${Math.floor(100 * progress * 0.3)}, ${Math.floor(50 + 50 * (1 - progress))}`;
    } else if (time < 0.7) {
      // Day - no overlay
      overlayAlpha = 0;
    } else if (time < 0.8) {
      // Sunset
      const progress = (time - 0.7) / 0.1;
      overlayAlpha = 0.2 * progress;
      overlayColor = `${Math.floor(255 * 0.3)}, ${Math.floor(100 * 0.2)}, ${Math.floor(50)}`;
    } else {
      // Night (dusk to midnight)
      const progress = (time - 0.8) / 0.2;
      overlayAlpha = 0.2 + 0.2 * progress;
      overlayColor = `0, 0, ${Math.floor(30 + 10 * progress)}`;
    }

    if (overlayAlpha > 0) {
      ctx.save();
      ctx.fillStyle = `rgba(${overlayColor}, ${overlayAlpha})`;
      ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);
      ctx.restore();
    }
  }

  /**
   * Render location glow effect
   */
  renderLocationGlow(ctx: CanvasRenderingContext2D, x: number, y: number, type: 'town' | 'castle' | 'dungeon' | 'cave' | 'shrine'): void {
    const colors: Record<string, string> = {
      town: '#ffaa00',
      castle: '#ffffff',
      dungeon: '#ff4444',
      cave: '#885533',
      shrine: '#4488ff',
    };

    const baseColor = colors[type];
    const pulse = Math.sin(this.ambientTime / 500) * 0.3 + 0.7;

    ctx.save();
    ctx.globalAlpha = 0.3 * pulse;

    // Outer glow
    const gradient = ctx.createRadialGradient(x + 8, y + 8, 0, x + 8, y + 8, 20);
    gradient.addColorStop(0, baseColor);
    gradient.addColorStop(1, 'transparent');
    ctx.fillStyle = gradient;
    ctx.fillRect(x - 12, y - 12, 40, 40);

    ctx.restore();
  }

  /**
   * Render encounter warning (flashing border when close to encounter)
   */
  renderEncounterWarning(ctx: CanvasRenderingContext2D, dangerLevel: number): void {
    if (dangerLevel < 0.7) return;

    const flash = Math.sin(this.ambientTime / 100) * 0.5 + 0.5;
    const alpha = (dangerLevel - 0.7) / 0.3 * flash * 0.3;

    ctx.save();
    ctx.strokeStyle = `rgba(255, 0, 0, ${alpha})`;
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, this.screenWidth - 4, this.screenHeight - 4);
    ctx.restore();
  }

  // Configuration methods
  setDayNightEnabled(enabled: boolean): void {
    this.enableDayNight = enabled;
  }

  setCloudsEnabled(enabled: boolean): void {
    this.enableClouds = enabled;
  }

  setParticlesEnabled(enabled: boolean): void {
    this.enableParticles = enabled;
  }

  setTimeOfDay(time: number): void {
    this.dayNightCycle = Math.max(0, Math.min(1, time));
  }

  getTimeOfDay(): number {
    return this.dayNightCycle;
  }
}
